import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Search, XCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { IMG } from "@/constants/images";

export default function Warranty() {
  const [cert, setCert] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const verify = async () => {
    if (!cert.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/warranty/verify/${encodeURIComponent(cert.trim())}`);
      setResult(data);
    } catch (_) { setResult({ found: false }); }
    finally { setBusy(false); }
  };

  const verifyUrl = `https://www.theerrah.com/warranty?id=${encodeURIComponent(cert.trim())}`;
  const qr = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&bgcolor=11100E&color=C9A45C&data=${encodeURIComponent(verifyUrl)}`;

  return (
    <div data-testid="warranty-page">
      <Seo title="Warranty Verification" description="Verify your ERRAH surface protection warranty and view your digital certificate." />
      <section className="relative min-h-[42vh] flex items-center overflow-hidden">
        <img src={IMG.marble} alt="Warranty" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/50" />
        <div className="relative max-w-[1500px] mx-auto px-5 lg:px-10">
          <p className="errah-label mb-3">Warranty Vault</p>
          <h1 className="serif-head text-4xl lg:text-6xl">Protection deserves peace of mind.</h1>
          <p className="mt-4 text-gray-300 max-w-xl">Enter your ERRAH warranty ID to verify authenticity and view certificate details.</p>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-5 lg:px-10 py-12">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <input value={cert} onChange={(e) => setCert(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verify()}
              placeholder="e.g. ERRAH-A1B2C3D4" data-testid="warranty-input"
              className="w-full bg-transparent border border-white/15 pl-9 pr-3 py-3 text-sm focus:border-[#C9A45C] uppercase" />
          </div>
          <button onClick={verify} disabled={busy} data-testid="warranty-verify" className="bg-[#C9A45C] text-black px-7 py-3 text-sm font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-60">{busy ? "…" : "VERIFY"}</button>
        </div>

        {result && !result.found && (
          <div className="mt-8 hair-border bg-warmblack p-8 text-center" data-testid="warranty-not-found">
            <XCircle className="mx-auto text-red-400" size={40} />
            <p className="mt-3 text-gray-300">No warranty found for that ID. Please check the certificate number and try again.</p>
          </div>
        )}

        {result && result.found && (
          <div className="mt-8 bg-warmblack border border-[#C9A45C]/40 p-8 fade-up" data-testid="warranty-found">
            <div className="flex items-center justify-between">
              <span className="font-serif text-2xl tracking-[0.2em]">ERRAH</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-[#10B981] border border-[#10B981]/40 px-3 py-1"><CheckCircle2 size={13} /> Verified</span>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-6 items-center">
              <div className="flex-1">
                <ShieldCheck className="text-[#C9A45C]" size={32} />
                <h2 className="serif-head text-3xl mt-3">{result.product}</h2>
                <p className="text-sm text-gray-400">{result.surface}</p>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <KV k="Certificate" v={result.certificate_no} />
                  <KV k="Holder" v={result.holder} />
                  <KV k="Installer" v={result.installer} />
                  <KV k="Install date" v={result.install_date} />
                  <KV k="Period" v={result.period} />
                  <KV k="Status" v={result.status} />
                </div>
              </div>
              <div className="text-center">
                <img src={qr} alt="Warranty QR" className="w-[150px] h-[150px] hair-border" />
                <p className="text-[10px] text-gray-600 mt-2">Scan to verify</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-10 text-center">
          <p className="text-sm text-gray-500">Are you an existing ERRAH customer?</p>
          <button onClick={() => navigate("/my-errah")} className="mt-2 text-sm text-[#C9A45C] hover:underline">View all your warranties in MY ERRAH →</button>
        </div>
      </section>
    </div>
  );
}

function KV({ k, v }) { return <div><p className="errah-label !text-[10px]">{k}</p><p className="text-gray-200">{v || "—"}</p></div>; }
